/**
 * Sysadmin Audit rail — TanStack Query hooks for PR-F (Phase 5).
 *
 * Backs the 2 new v1 sub-tabs:
 *   - /sysadmin/audit/exports   → useAuditExports + useCreateAuditExport + useAuditExport
 *   - /sysadmin/audit/witness   → useAuditWitnessConfig + useUpdateAuditWitnessConfig
 *                                  + useAuditWitnessRecords
 *
 * Built on top of `createSysadminQuery` + `createSysadminMutation` from
 * use-sysadmin-query.ts. Schemas live HERE (vs duplicated across
 * routes) so the wire types stay in lockstep with the Fastify route
 * schemas — manual sync today, codegen target in v1.1.
 *
 * Refresh cadence:
 *   - Exports list:   5s (queued/running bundles tick fast)
 *   - Single bundle:  5s (poll status while not complete)
 *   - Witness config: 30s (rarely changes; manual save shortens via refetch)
 *   - Records list:   30s (WORM table grows on 24h cadence)
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminStepUpMutation,
} from './use-sysadmin-query';

// ════════════════════════════════════════════════════════════════════════
//  Compliance Exports
// ════════════════════════════════════════════════════════════════════════

export const AuditExportFrameworkSchema = z.enum([
  'itar',
  'cmmc_l2',
  'hipaa',
  'gdpr',
  'eu_ai_act',
  'platform_full',
]);
export type AuditExportFramework = z.infer<typeof AuditExportFrameworkSchema>;

export const AuditExportScopeSchema = z.enum(['platform', 'tenant']);
export type AuditExportScope = z.infer<typeof AuditExportScopeSchema>;

export const AuditExportStatusSchema = z.enum([
  'queued',
  'running',
  'complete',
  'failed',
]);
export type AuditExportStatus = z.infer<typeof AuditExportStatusSchema>;

export const AuditExportRowSchema = z.object({
  id: z.string().uuid(),
  framework: AuditExportFrameworkSchema,
  fromTs: z.string(),
  toTs: z.string(),
  scope: AuditExportScopeSchema,
  tenantId: z.string().uuid().nullable(),
  status: AuditExportStatusSchema,
  signedAt: z.string().nullable(),
  bundleSizeBytes: z.number().nullable(),
  eventCount: z.number().int().nullable(),
  hashChainAnchorFirst: z.string().nullable(),
  hashChainAnchorLast: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  // #1128 — Server-supplied filename for the on-disk bundle. Honoured
  // by the UI download trigger so legacy `.tar.gz` bundles keep their
  // historical filename and new bundles ship as `.argusai-bundle.gz`.
  downloadKey: z.string().nullable(),
  cursor: z.string(),
});
export type AuditExportRow = z.infer<typeof AuditExportRowSchema>;

export const AuditExportListResponseSchema = z.object({
  items: z.array(AuditExportRowSchema),
  nextCursor: z.string().nullable(),
});
export type AuditExportListResponse = z.infer<typeof AuditExportListResponseSchema>;

export const useAuditExports = createSysadminQuery({
  key: ['sysadmin-audit-exports'],
  url: '/admin/platform/audit/exports',
  schema: AuditExportListResponseSchema,
  // 5s — operators need to see status transitions promptly while a
  // bundle is generating. Drops back to the default 10s once everything
  // is complete (refetchInterval doesn't auto-throttle; UI components
  // re-trigger a fresh useQuery when entering a screen).
  refreshMs: 5_000,
});

export const AuditExportDetailSchema = AuditExportRowSchema.omit({
  cursor: true,
}).extend({
  signature: z.string().nullable(),
  signaturePubkeyId: z.string().nullable(),
  downloadKey: z.string().nullable(),
  verification: z.object({
    algorithm: z.literal('Ed25519'),
    signatureFormat: z.literal('hex'),
    bundleFormat: z.literal('audit-export-bundle/v1'),
    signedOver: z.literal('SHA-256 of the unzipped bundle file'),
  }),
});
export type AuditExportDetail = z.infer<typeof AuditExportDetailSchema>;

export const CreateAuditExportInputSchema = z.object({
  framework: AuditExportFrameworkSchema,
  fromTs: z.string().datetime(),
  toTs: z.string().datetime(),
  scope: AuditExportScopeSchema,
  tenantId: z.string().uuid().nullable().optional(),
});
export type CreateAuditExportInput = z.infer<typeof CreateAuditExportInputSchema>;

export const CreateAuditExportResponseSchema = z.object({
  bundleId: z.string().uuid(),
  status: z.literal('queued'),
});
export type CreateAuditExportResponse = z.infer<
  typeof CreateAuditExportResponseSchema
>;

// Tier-1 step-up: queueing a compliance export is the first step of releasing
// audit evidence and requires a fresh-TOTP step-up + X-Step-Up-Reason. The
// prior non-step-up factory sent no reason → 400 REASON_REQUIRED every time
// (platform audit 2026-06-10). Caller passes { input, stepUpReason }.
export const useCreateAuditExport = createSysadminStepUpMutation<
  CreateAuditExportInput,
  CreateAuditExportResponse
>({
  url: '/admin/platform/audit/exports',
  method: 'POST',
  inputSchema: CreateAuditExportInputSchema,
  outputSchema: CreateAuditExportResponseSchema,
  invalidates: [['sysadmin-audit-exports']],
});

/**
 * Maximum span of a single bundle's time range (90 days). Surfaced in
 * the UI's date-range form so the picker can client-side enforce.
 */
export const AUDIT_EXPORT_MAX_RANGE_DAYS = 90;

// ════════════════════════════════════════════════════════════════════════
//  Witness Configuration
// ════════════════════════════════════════════════════════════════════════

export const AUDIT_WITNESS_CADENCE_MIN_HOURS = 1;
export const AUDIT_WITNESS_CADENCE_MAX_HOURS = 168;

export const AuditWitnessConfigSchema = z.object({
  id: z.string().uuid().nullable(),
  enabled: z.boolean(),
  witnessUrl: z.string().nullable(),
  witnessPubkeyId: z.string().uuid().nullable(),
  witnessPubkeyAlias: z.string().nullable(),
  batchCadenceHours: z.number().int(),
  lastSuccessAt: z.string().nullable(),
  lastAttemptAt: z.string().nullable(),
  lastError: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().uuid().nullable(),
});
export type AuditWitnessConfig = z.infer<typeof AuditWitnessConfigSchema>;

export const useAuditWitnessConfig = createSysadminQuery({
  key: ['sysadmin-audit-witness-config'],
  url: '/admin/platform/audit/witness/config',
  schema: AuditWitnessConfigSchema,
  refreshMs: 30_000,
});

export const PatchWitnessConfigInputSchema = z.object({
  enabled: z.boolean().optional(),
  witnessUrl: z.string().url().nullable().optional(),
  witnessPubkeyId: z.string().uuid().nullable().optional(),
  batchCadenceHours: z
    .number()
    .int()
    .min(AUDIT_WITNESS_CADENCE_MIN_HOURS)
    .max(AUDIT_WITNESS_CADENCE_MAX_HOURS)
    .optional(),
});
export type PatchWitnessConfigInput = z.infer<
  typeof PatchWitnessConfigInputSchema
>;

// Tier-1 step-up: witness config drives the external tamper-evidence pipeline
// (flipping enabled, repointing witnessUrl, rotating the pubkey), so it requires
// a fresh-TOTP step-up + X-Step-Up-Reason. The prior non-step-up factory sent no
// reason → 400 REASON_REQUIRED (platform audit 2026-06-10). Caller passes
// { input, stepUpReason }.
export const useUpdateAuditWitnessConfig = createSysadminStepUpMutation<
  PatchWitnessConfigInput,
  AuditWitnessConfig
>({
  url: '/admin/platform/audit/witness/config',
  method: 'PATCH',
  inputSchema: PatchWitnessConfigInputSchema,
  outputSchema: AuditWitnessConfigSchema,
  invalidates: [['sysadmin-audit-witness-config']],
});

export const AuditWitnessRecordRowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  chainHeadSeq: z.string(),
  chainHeadHash: z.string(),
  witnessSignature: z.string(),
  witnessPubkeyId: z.string(),
  witnessedAt: z.string(),
  syncedToExternal: z.boolean(),
  externalWitnessUrl: z.string().nullable(),
  cursor: z.string(),
});
export type AuditWitnessRecordRow = z.infer<typeof AuditWitnessRecordRowSchema>;

export const AuditWitnessRecordsListSchema = z.object({
  items: z.array(AuditWitnessRecordRowSchema),
  nextCursor: z.string().nullable(),
});
export type AuditWitnessRecordsList = z.infer<
  typeof AuditWitnessRecordsListSchema
>;

export const useAuditWitnessRecords = createSysadminQuery({
  key: ['sysadmin-audit-witness-records'],
  url: '/admin/platform/audit/witness/records?limit=20',
  schema: AuditWitnessRecordsListSchema,
  refreshMs: 30_000,
});

// ════════════════════════════════════════════════════════════════════════
//  Framework metadata (UI labels + bundle-fit hints)
// ════════════════════════════════════════════════════════════════════════

/**
 * UI labels + retention-policy hints per OQ-5 framework. The retention
 * column is informational only — the bundle file's manifest carries the
 * authoritative retention metadata at the row level.
 */
export const AUDIT_EXPORT_FRAMEWORK_META: Record<
  AuditExportFramework,
  { label: string; retentionYears: number | null; description: string }
> = {
  itar: {
    label: 'ITAR',
    retentionYears: 5,
    description: 'International Traffic in Arms Regulations',
  },
  cmmc_l2: {
    label: 'CMMC L2',
    retentionYears: 3,
    description: 'Cybersecurity Maturity Model Certification, Level 2',
  },
  hipaa: {
    label: 'HIPAA',
    retentionYears: 6,
    description: 'Health Insurance Portability and Accountability Act',
  },
  gdpr: {
    label: 'GDPR',
    retentionYears: 6,
    description: 'General Data Protection Regulation',
  },
  eu_ai_act: {
    label: 'EU AI Act',
    retentionYears: 10,
    description: 'EU Artificial Intelligence Act',
  },
  platform_full: {
    label: 'Platform Full',
    retentionYears: null,
    description: 'Catch-all — includes every audit category',
  },
};
